'use client';
import { ComponentProps, useContext } from 'react';
// eslint-disable-next-line no-restricted-imports
import type { Button } from '@alto-avios/alto-ui';

import { matchesAnyPattern } from '../../utilities';

import { RouterContext } from './RouterContext.ts';
import { RouterProviderProps } from './RouterProvider.tsx';

export interface UseRouterLinkProps {
  href?: string | undefined;
  disableRouting?: boolean;
}

const shouldUseCustomRouterLink = ({
  href,
  router,
  disableRouting,
}: {
  href: string;
  router: RouterProviderProps;
  disableRouting: boolean;
}): boolean => {
  if (disableRouting) {
    return false;
  }

  const url = new URL(href);
  const subpath = `${url.pathname}${url.search}`;

  if (window.location.origin !== url.origin) {
    return true;
  }

  return !matchesAnyPattern(subpath, router.blacklist || []);
};

export const getLinkProps = ({
  href,
  router,
  disableRouting = false,
}: UseRouterLinkProps & { router?: RouterProviderProps }): ComponentProps<typeof Button> => {
  // elementProps.as - controls which react component is used to render alto-ui button
  // elementProps.elementType - informs react-aria how to treat the component in terms of adding HTML props, e.g. `a` required for href

  if (!href) {
    return {
      as: 'button',
      elementType: 'button',
    };
  }

  const elementProps: ComponentProps<typeof Button> = {
    as: 'a',
    elementType: 'a',
    href,
    preventFocusOnPress: true,
  };

  if (!router || !shouldUseCustomRouterLink({ href, router, disableRouting })) {
    return elementProps;
  }

  // want to use custom component, but still treated as an `a` tag for prop management via as=a
  elementProps.as = router.LinkComponent;

  // add the extra href prop when requested in router settings.
  // this allows for react-router-dom -like links <Link to="/something" />
  // when `hrefProp: 'to'`
  if (router.hrefProp && router.hrefProp !== 'href') {
    elementProps.passthroughProps = {
      [router.hrefProp]: href,
    };
  }

  return elementProps;
};

export const useRouterLink = ({ href, disableRouting }: UseRouterLinkProps): ComponentProps<typeof Button> => {
  const router = useContext(RouterContext);

  return getLinkProps({ href, disableRouting, router });
};
